/* storyScript.js - every word in the teacher hero (/teachers-preview, dev only).

   The hero is a SIMPLIFIED story of the real features, played by Buffy (the
   only character on the page) on a colorful backdrop. No props, no metaphors:
   each step shows the feature itself as clean product UI (owner, 2026-09-29).

   Copy rules: no em dashes, no exclamation marks, no invented numbers, no
   publisher names. The draft names no question count (the teacher asked for
   none; critique 2026-09-30) and is titled like step 3's next step; "11 chapters"
   is the real Algebra 1 chapter count (10 carry SAT skills, chapter 2 does
   not: sections/satCourses.json); "Due Friday, 11:59 PM" matches the
   product's default due time (23:59). Math in step 3 is checked:
   3(x - 2) = 2x + 5 gives x = 11 (C); giving the 3 to the x only gives
   3x - 2 = 2x + 5, so x = 7 (B). Minus signs are U+2212.

   SAMPLE DATA CANON (lead, 2026-09-29; DECISIONS.md): levels are 1 to 7 per
   SAT skill everywhere (never percent). Step 2 class levels 2.6 / 4.1 / 5.0
   (weakest: solving linear equations); step 5 the same skills over 4 weeks,
   2.6 -> 3.5, 4.1 -> 4.6, 5.0 -> 5.3. */

import SAT_COURSES from './sections/satCourses.json';
import { SURVEY_PATH } from './survey/surveyData';

export const HEADLINE = { lead: 'Connecting the SAT', tail: 'to your classroom' };
// owner 2026-09-29: step 1 is "Choose your curriculum" (the drag-in upload is not built yet)
export const SUBLINE = 'Choose your curriculum and see where the SAT shows up. Free for teachers.';
// owner, 2026-09-29: the teacher CTA says "Set up my class" everywhere (matches the nav).
// 2026-09-30: it opens the start survey first (survey/), which ends at the signup.
export const CTA = { label: 'Set up my class', to: SURVEY_PATH };

// The fake composer's placeholder mirrors the product's BuffyComposer default;
// the phone composer is too narrow for it, so it says the short form (never
// an ellipsis-truncated line).
export const PLACEHOLDER = 'Ask Buffy about your class…';
export const PLACEHOLDER_SHORT = 'Ask Buffy…';

/* Chapter titles from src/TeacherDashboardV3/curriculum/curriculumFixture.json
   (Algebra 1, 11 chapters). No publisher name anywhere on the page. */
export const COURSE = {
  name: 'Algebra 1',
  // The chip is the course itself, not an uploaded file (2026-10-28, Sandy:
  // upload is off in production; teachers pick from our list).
  file: 'Algebra 1',
  // the phone's chip (the full name does not fit a 343 px stage)
  fileShort: 'Algebra 1',
  fileKind: 'Course',
  chapters: [
    'Solving Linear Equations',
    'Solving Linear Inequalities',
    'Graphing Linear Functions',
    'Writing Linear Functions',
    'Solving Systems of Linear Equations',
    'Exponential Functions and Sequences',
    'Polynomial Equations and Factoring',
    'Graphing Quadratic Functions',
    'Solving Quadratic Equations',
    'Radical Functions and Equations',
    'Data Analysis and Displays',
  ],
};

/* The bridge: three chapters and the SAT skill each one directly carries.
   Read from the SAME data the "Any curriculum" section uses
   (sections/satCourses.json, Algebra 1: [n, chapter, skill, domain, ...]), so
   the page never names two different skills for one chapter. Colors are the
   student app's math domain colors (StudentSubjectPage DOMAIN_COLORS). */
export const DOMAIN = {
  algebra: { dot: '#2563EB', tint: '#E9F0FE', ink: '#1E40AF', ring: '#C9D8FB' },
  advanced: { dot: '#3B82F6', tint: '#EBF3FF', ink: '#1D4ED8', ring: '#CFE0FD' },
  psda: { dot: '#60A5FA', tint: '#EEF6FF', ink: '#1E4F9A', ring: '#D3E6FD' },
  geotrig: { dot: '#1E40AF', tint: '#E8ECF8', ink: '#16308C', ring: '#C8D1EE' },
};
const ALGEBRA_1 = (SAT_COURSES.find ? SAT_COURSES : Object.values(SAT_COURSES))
  .find((c) => c.id === 'algebra-1') || { chapters: [] };
const pairOf = (n, fallback) => {
  const row = ALGEBRA_1.chapters.find((r) => r[0] === n);
  return row && row[2] ? { chapter: n, skill: row[2], domain: row[3] || 'algebra' } : fallback;
};
// `short`: the phone's row title (the full skill name would be cut off)
const SHORT = { 1: 'Solving linear equations', 4: 'Linear word problems', 8: 'Quadratic graphs' };
export const BRIDGE = [
  pairOf(1, { chapter: 1, skill: 'Solving linear equations and inequalities', domain: 'algebra' }),
  pairOf(4, { chapter: 4, skill: 'Linear relationship word problems', domain: 'algebra' }),
  pairOf(8, { chapter: 8, skill: 'Quadratic graphs', domain: 'advanced' }),
].map((b) => ({ ...b, short: SHORT[b.chapter] || b.skill, ...(DOMAIN[b.domain] || DOMAIN.algebra) }));

/* The chapters that carry an SAT skill (10 of the 11; the same data the
   "Any curriculum" section reads). Step 1 marks them as Buffy reads the
   course; the others stay gray. */
export const LINKED_CHAPTERS = COURSE.chapters
  .map((_, i) => i + 1)
  .filter((n) => {
    const row = ALGEBRA_1.chapters.find((r) => r[0] === n);
    return !row || !!row[2];
  });

/* Step 1 (owner, 2026-09-30: "the number must match what is shown"): EVERY
   chapter that carries an SAT skill, with its skill, in chapter order: the
   same direct links the "Any curriculum" section shows (satCourses.json), so
   the page agrees everywhere. 10 of the 11 Algebra 1 chapters. */
export const LINKS = ALGEBRA_1.chapters
  .filter((r) => r[2])
  .map((r) => ({ chapter: r[0], skill: r[2], domain: r[3] || 'algebra', ...(DOMAIN[r[3]] || DOMAIN.algebra) }));

/* The level scale (canon): 1 to 7 per SAT skill. */
export const MAX_LEVEL = 7;

/* Step 2: the class's assessment results on the connected skills
   (illustrative sample data, approved by the owner 2026-09-29): the class's
   average level on each, 1 to 7, as in the Assessments section. The lowest
   row is chapter 1's skill, the one step 3 opens. */
export const RESULTS = {
  title: 'Assessment results',
  meta: 'Average level, 1 to 7',
  rows: [
    { chapter: 1, level: 2.6, weak: true },
    { chapter: 4, level: 4.1 },
    { chapter: 8, level: 5.0 },
  ],
  weakLabel: 'Lowest',
};

/* Step 4: beside the draft, the adaptive practice every student also gets. */
export const ADAPTIVE = {
  title: 'Adaptive practice',
  state: 'On',
  line: 'Each student also practices at their own level.',
};

/* Step 5: the same skills, 4 weeks later, on the same 1 to 7 scale (one bar
   per row: the start level, then the gain); the skill that improved most
   (chapter 1, +0.9) is highlighted. */
export const PROGRESS = {
  title: 'Class progress',
  meta: 'Last 4 weeks',
  rows: [
    { chapter: 1, from: 2.6, to: 3.5, best: true },
    { chapter: 4, from: 4.1, to: 4.6 },
    { chapter: 8, from: 5.0, to: 5.3 },
  ],
};

export const DRAFT = {
  bar: 'Assignment',
  title: 'Practice on distributing',
  rows: ['At each student’s level', 'Due Friday, 11:59 PM'],
  klass: 'Period 3 Algebra 1',
  draft: 'Draft',
  done: 'Assigned',
  assign: 'Assign',
};

export const QUESTION = {
  bar: 'Question',
  topic: 'Solving linear equations',
  stem: '3(x − 2) = 2x + 5. What is x?',
  choices: [
    { key: 'A', value: '3', share: 0.1 },
    { key: 'B', value: '7', share: 0.58 },
    { key: 'C', value: '11', share: 0.28, correct: true },
    { key: 'D', value: '13', share: 0.04 },
  ],
  most: 'Most picked',
  correct: 'Correct',
  whyLabel: 'Why B',
  whyFrom: '3(x − 2)',
  whyTo: '3x ',
  whyMiss: '− 2',
};

/* Step 3, right half: Buffy's explanation beside the example (owner). */
export const EXPLAIN = {
  label: 'Buffy',
  text: 'They gave the 3 to the x, but not to the 2.',
  next: 'Next step',
  nextLine: 'Practice on distributing, ready to assign',
  // the phone's narrower half (step 3 side by side)
  nextShort: 'Practice on distributing',
};

/* The five steps (owner, 2026-09-29, confirmed): each is three beats. ASK:
   the chat sits in the middle of the stage, larger, and the teacher's
   `teacher` line types into it and is sent. ANSWER: Buffy replies with
   `buffy`. SHOW: the chat eases down to the dock and the step's scene plays
   above it. `label` is the progress-row name (plain words, no numbers, no
   "Step 1 of 5"); `after` replaces his line once the teacher clicks Assign. */
export const STEPS = [
  {
    id: 'curriculum',
    label: 'Choose your curriculum',
    teacher: 'Here’s the curriculum I teach.',
    buffy: 'Got it. 10 of your 11 chapters have SAT skills.',
  },
  {
    id: 'struggle',
    label: 'See where they struggle',
    teacher: 'How is my class doing on these skills?',
    buffy: 'Here’s what your assessment shows.',
  },
  {
    id: 'why',
    label: 'See why',
    teacher: 'Why are they missing it?',
    // short: the full explanation lives only in the right-hand card
    buffy: 'Most of the class picked B. Here’s why.',
  },
  {
    id: 'practice',
    label: 'Practice',
    teacher: 'Assign practice on that, due Friday.',
    buffy: 'I can do that for you. Here’s your draft.',
    after: 'Assigned to Period 3. Due Friday.',
  },
  {
    id: 'progress',
    label: 'Improve over time',
    teacher: 'Are they getting better?',
    buffy: 'Yes. Here’s your class over the last 4 weeks.',
  },
];

export const STORY_LABEL = `How Brain Buffs connects the SAT to your classroom, in ${STEPS.length} steps`;
